import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Input,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Popconfirm,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiEyeDuotone,
	PiHouseDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import {
	requestGetServiceList,
	requestGetServiceGroupList,
	requestDeleteService,
} from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title } = Typography;
const { Option } = Select;

function ServicesListPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [services, setServices] = useState([]);
	const [serviceGroups, setServiceGroups] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		groupId: searchParams.get("groupId") || "",
		status: searchParams.get("status") || "",
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Services";
		fetchServiceGroups();
		fetchServices();
	}, []);

	useEffect(() => {
		fetchServices();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.groupId) params.set("groupId", filters.groupId);
		if (filters.status) params.set("status", filters.status);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchServiceGroups = () => {
		requestGetServiceGroupList({
			limit: 100,
			page: 1,
			order: "name:asc",
		})
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const serviceGroupsData = response.data.data || [];
				setServiceGroups(serviceGroupsData);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch service groups",
				});
			});
	};

	const fetchServices = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		if (filters.groupId) {
			filterParts.push(`groupId:${filters.groupId}`);
		}
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		// Add more filter fields here as needed

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "_id:desc",
		};

		requestGetServiceList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const servicesData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setServices(servicesData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch services",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSearch = (value) => {
		setFilters((prev) => ({ ...prev, keyword: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			...pagination,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	const getServiceGroupName = (groupId) => {
		const group = serviceGroups.find((g) => g._id === groupId);
		return group ? group.name : "Unknown";
	};

	const columns = [
		{
			title: "Code",
			dataIndex: "code",
			key: "code",
			width: 120,
			render: (text) => <strong>{text}</strong>,
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
						{record.description?.length > 80
							? `${record.description.substring(0, 80)}...`
							: record.description}
					</div>
				</div>
			),
		},
		{
			title: "Group",
			dataIndex: "groupId",
			key: "groupId",
			width: 150,
			render: (groupId) => getServiceGroupName(groupId),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status) => (
				<Tag color={status === "ACTIVE" ? "success" : "error"}>
					{status === "ACTIVE" ? "Active" : "Inactive"}
				</Tag>
			),
		},
		{
			title: "Plans",
			dataIndex: "plans",
			key: "plans",
			width: 80,
			render: (plans) => plans?.length || 0,
		},
		{
			title: "Features",
			dataIndex: "features",
			key: "features",
			width: 80,
			render: (features) => features?.length || 0,
		},
		{
			title: "Actions",
			key: "actions",
			align: "right",
			width: 150,
			render: (_, record) => (
				<Space size="small">
					<Button
						type="text"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/services/${record._id}`)}
						title="View Details"
					/>
					<Button
						type="text"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/services/${record._id}/edit`)}
						title="Edit Service"
					/>
					<Popconfirm
						title="Delete Service"
						description="Are you sure you want to delete this service?"
						onConfirm={() => handleDelete(record._id)}
						okText="Yes"
						cancelText="No">
						<Button type="text" danger icon={<PiTrashDuotone />} title="Delete Service" />
					</Popconfirm>
				</Space>
			),
		},
	];

	const handleDelete = (id) => {
		setLoading(true);
		requestDeleteService(id)
			.then(() => {
				message.success("Service deleted successfully");
				fetchServices(); // Refresh the list
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to delete service",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Services Management
					</Title>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title="All Services"
					extra={
						<Space>
							<SearchInput
								placeholder="Search services..."
								value={filters.keyword}
								onChange={handleSearch}
								style={{ width: 250 }}
							/>
							<Button
								type="primary"
								icon={<PiPlusDuotone />}
								onClick={() => navigate("/services/add")}>
								Add Service
							</Button>
						</Space>
					}>
					{/* Filters */}
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col xs={24} sm={12} md={8}>
							<Select
								placeholder="Filter by group"
								style={{ width: "100%" }}
								value={filters.groupId || undefined}
								allowClear
								onChange={(value) => handleFilterChange("groupId", value)}>
								{serviceGroups.map((group) => (
									<Option key={group._id} value={group._id}>
										{group.name}
									</Option>
								))}
							</Select>
						</Col>
						<Col xs={24} sm={12} md={8}>
							<Select
								placeholder="Filter by status"
								style={{ width: "100%" }}
								value={filters.status || undefined}
								allowClear
								onChange={(value) => handleFilterChange("status", value)}>
								<Option value="ACTIVE">Active</Option>
								<Option value="INACTIVE">Inactive</Option>
							</Select>
						</Col>
					</Row>

					<Table
						columns={columns}
						dataSource={services}
						rowKey="_id"
						loading={loading}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pageSize,
							total: pagination.total,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} services`,
						}}
						onChange={handleTableChange}
						scroll={{ x: 1000 }}
					/>
				</Card>
			</div>
		</div>
	);
}

export default ServicesListPage;
